import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import DevicesHeader from './DowntimeLogHeader';
import { useMediaQuery } from 'react-responsive';
import SensorSelect from '../../components/SensorSelect';
import CompanySelector from '../../components/CompanySelector';
import useSensorOptions from '../../hooks/useSensorOptions';
import useCompanyOptions from '../../hooks/useCompanyOptions';
import useSelectedCompany from '../../hooks/useSelectedCompany';
import useSelectedSensor from '../../hooks/useSelectedSensor';
import { useSelector } from 'react-redux';
import DowntimeLogLabel from './DowntimeLogLabel';
import { useDowntimeQuery } from '../../services/api';
import DowntimeTable from './DowntimeTable';
import useSelectedGroup from '../../hooks/useSelectedGroup';
import useGroupOptions from '../../hooks/useGroupOptions';
import GroupSelector from '../../components/GroupSelector';

const DeviceStatusLog = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery({ maxWidth: 480 });
    const { company, role } = useSelector((state) => state.global);
    const isSuperOrPowerAdmin = role === 'Super Admin' || role === 'Power Admin';

    // Company, device and group options
    const { companyOptions } = useCompanyOptions();
    const { selectedCompany, selectedCompanyId, handleCompanyChange } = useSelectedCompany({ companyOptions });
    const groupOptions = useGroupOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const sensorOptions = useSensorOptions(isSuperOrPowerAdmin ? selectedCompanyId : company);
    const { selectedGroup, handleGroupChange } = useSelectedGroup(groupOptions, isSuperOrPowerAdmin ? selectedCompanyId : company);
    const filteredSensorOptions = useMemo(() => {
        if (!groupOptions.length) {
            return sensorOptions;
        }

        const selectedGroupObj = groupOptions.find((group) => group._id === selectedGroup);
        const selectedDeviceIds = selectedGroupObj?.deviceIds || [];

        return sensorOptions.filter((sensor) => selectedDeviceIds.includes(sensor._id));
    }, [sensorOptions, groupOptions, selectedGroup]);
    const { selectedSensor, handleSensorChange, getSelectedSensorLabel } = useSelectedSensor(filteredSensorOptions, isSuperOrPowerAdmin ? selectedCompany : company);

    // API
    const { data: downtimeData = [] } = useDowntimeQuery(selectedSensor, { skip: !selectedSensor, refetchOnMountOrArgChange: true });

    const hasGroups = Array.isArray(groupOptions) && groupOptions.length > 0;

    return (
        <Box m={isSmallScreen ? '1rem' : '1rem 2.5rem'}>
            {isSmallScreen ? (
                <>
                    <DevicesHeader theme={theme} />
                    <Box
                        display="flex"
                        flexDirection={isSmallScreen ? 'column' : 'row'}
                        justifyContent="space-between"
                        alignItems="center"
                        gap="1rem"
                        marginBottom="1rem"
                        padding="1rem"
                        backgroundColor={theme.palette.background.paper}
                        borderRadius="15px"
                        sx={{
                            boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                        }}
                    >
                        {isSuperOrPowerAdmin && <CompanySelector selectedCompany={selectedCompany} handleCompanyChange={handleCompanyChange} companyOptions={companyOptions} />}
                        {hasGroups && <GroupSelector selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />}
                        <SensorSelect sensorOptions={filteredSensorOptions} selectedSensor={selectedSensor} handleSensorChange={handleSensorChange} isSmallScreen={isSmallScreen} />
                    </Box>
                </>
            ) : (
                <DevicesHeader theme={theme}>
                    {isSuperOrPowerAdmin && <CompanySelector selectedCompany={selectedCompany} handleCompanyChange={handleCompanyChange} companyOptions={companyOptions} />}
                    {hasGroups && <GroupSelector selectedGroup={selectedGroup} handleGroupChange={handleGroupChange} groupOptions={groupOptions} />}
                    <SensorSelect sensorOptions={filteredSensorOptions} selectedSensor={selectedSensor} handleSensorChange={handleSensorChange} isSmallScreen={isSmallScreen} />
                </DevicesHeader>
            )}
            <DowntimeLogLabel sensorLabel={getSelectedSensorLabel()} isSmallScreen={isSmallScreen} />
            <DowntimeTable downtimeData={downtimeData} />
        </Box>
    );
};

export default DeviceStatusLog;
